import type { RawMessage } from "@/api/messages";
import type { DynamicPricingOffer } from "@/api/offers";
import type { PmUser } from "@/api/user";
import { getIsResend as getIsResendFlag } from "@/lib/flags";

// Kill lever for typed payment-state rendering (slice 2.2): in-code ON;
// env PAYMENT_STATE_UI_ENABLED="false"/"0" disables, restoring the
// historical object-presence inference.
const paymentStateUiEnabled = (): boolean => {
  try {
    const v = (window as any)?.Penmate?.env?.paymentStateUiEnabled;
    if (typeof v === "string" && v.trim()) {
      const s = v.trim().toLowerCase();
      return s !== "false" && s !== "0";
    }
    return true;
  } catch {
    return true;
  }
};

export type CheckoutSectionKey =
  | "return_address"
  | "postcard_front"
  | "photo_type"
  | "delivery"
  | "facility"
  | "credits"
  | "payment";

export type CheckoutSection = {
  key: CheckoutSectionKey;
  title: string;
  required: boolean;
  complete: boolean;
  detail?: string;
};

export type CheckoutRequirements = {
  sections: CheckoutSection[];
  nextRequiredKey: CheckoutSectionKey | null;
  paymentRequired: boolean;
  paymentFailureState: "attached_retry_eligible" | "attached_replacement_required" | null;
  creditsPurchaseRequired: boolean;
  creditsDue: number;
  creditsAvailable: number;
};

const toLowerString = (value: unknown): string => {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const toBool = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
  }
  return fallback;
};

const getMessageType = (letter: RawMessage | null | undefined): string => {
  const t = (letter as any)?.messageType ?? (letter as any)?.data?.messageType ?? (letter as any)?.order_type;
  const s = toLowerString(t);
  return s === "postcard" ? "postcard" : "letter";
};

const getShippingMethod = (letter: RawMessage | null | undefined): string => {
  const v = (letter as any)?.shippingMethod ?? (letter as any)?.data?.shippingMethod;
  const s = toLowerString(v);
  if (s === "emessage") return "emessage";
  return "letter";
};

const getSelectedPhotosCount = (letter: RawMessage | null | undefined): number => {
  const v = (letter as any)?.selectedPhotos ?? (letter as any)?.data?.selectedPhotos ?? (letter as any)?.photos;
  return Array.isArray(v) ? v.length : 0;
};

const getPostcardFrontUrl = (letter: RawMessage | null | undefined): string => {
  if (!letter) return "";
  const v =
    (letter as any)?.postcard?.frontImage?.url ??
    (letter as any)?.postcard?.frontImageUrl ??
    (letter as any)?.postcard?.front_image_url ??
    (letter as any)?.data?.postcard?.frontImage?.url ??
    (letter as any)?.data?.postcard?.frontImageUrl ??
    (letter as any)?.data?.postcard?.front_image_url;
  return typeof v === "string" ? v : "";
};

const hasFacilitySelected = (letter: RawMessage | null | undefined): boolean => {
  const eMessage = (letter as any)?.eMessage ?? (letter as any)?.data?.eMessage;
  if (!eMessage) return false;
  const id = (eMessage as any)?.id;
  const name = (eMessage as any)?.name;
  return (typeof id === "number" && id > 0) || (typeof id === "string" && id) || (typeof name === "string" && name);
};

const hasPhysicalReturnAddress = (letter: RawMessage | null | undefined): boolean => {
  const ra = (letter as any)?.returnAddress ?? (letter as any)?.data?.returnAddress;
  if (!ra || typeof ra !== "object") return false;

  const getCountryRequiredFields = (country: string): { state: boolean; postalCode: boolean } => {
    const cc = country.toUpperCase();
    const requirements: Record<string, { state: boolean; postalCode: boolean }> = {
      US: { state: true, postalCode: true },
      CA: { state: true, postalCode: true },
      AU: { state: true, postalCode: true },
      GB: { state: false, postalCode: true },
      DE: { state: false, postalCode: true },
      FR: { state: false, postalCode: true },
      IT: { state: false, postalCode: true },
      ES: { state: false, postalCode: true },
      NL: { state: false, postalCode: true },
      SE: { state: false, postalCode: true },
      NO: { state: false, postalCode: true },
      DK: { state: false, postalCode: true },
      FI: { state: false, postalCode: true },
      IE: { state: false, postalCode: true },
      AT: { state: false, postalCode: true },
      BE: { state: false, postalCode: true },
      PT: { state: false, postalCode: true },
      JP: { state: true, postalCode: true },
      KR: { state: false, postalCode: true },
      SG: { state: false, postalCode: true },
      HK: { state: false, postalCode: false },
      IN: { state: true, postalCode: true },
      BR: { state: true, postalCode: true },
      MX: { state: true, postalCode: true },
      DEFAULT: { state: false, postalCode: true },
    };
    return requirements[cc] || requirements.DEFAULT;
  };

  const firstName = (ra as any)?.firstName ?? (ra as any)?.first_name;
  const lastName = (ra as any)?.lastName ?? (ra as any)?.last_name;
  const name = (ra as any)?.name;
  const addressLine1 = (ra as any)?.address_line1 ?? (ra as any)?.addressLine1;
  const addressCity = (ra as any)?.address_city ?? (ra as any)?.addressCity;
  const addressState = (ra as any)?.address_state ?? (ra as any)?.addressState;
  const addressZip = (ra as any)?.address_zip ?? (ra as any)?.addressZip;
  const addressCountry = (ra as any)?.address_country ?? (ra as any)?.country ?? "US";
  const requirements = getCountryRequiredFields(toLowerString(addressCountry) || "US");

  const hasName =
    (typeof name === "string" && name.trim().length > 0) ||
    (typeof firstName === "string" &&
      firstName.replace(/\./g, "").trim().length > 1 &&
      typeof lastName === "string" &&
      lastName.trim().length > 0);

  const hasAddressBasics =
    typeof addressLine1 === "string" &&
    addressLine1.trim().length > 0 &&
    typeof addressCity === "string" &&
    addressCity.trim().length > 0 &&
    (!requirements.state || (typeof addressState === "string" && addressState.trim().length > 0)) &&
    (!requirements.postalCode || (typeof addressZip === "string" && addressZip.trim().length > 0));

  return hasName && hasAddressBasics;
};

export const getLetterPhotoType = (letter: RawMessage | null | undefined): string => {
  const v =
    (letter as any)?.photoType ??
    (letter as any)?.photo_type ??
    (letter as any)?.data?.photoType ??
    (letter as any)?.data?.photo_type;
  const s = toLowerString(v);
  // Backend sometimes returns legacy sub-types (e.g. "3x5") that should be treated as "glossy" in UI.
  if (s === "3x5") return "glossy";
  return s;
};

const getPaymentDetail = (letter: RawMessage | null | undefined): string | undefined => {
  const payment = (letter as any)?.payment ?? (letter as any)?.data?.payment;
  if (!payment || typeof payment !== "object") return undefined;

  const brand = toLowerString((payment as any)?.brand ?? (payment as any)?.type);
  const last4 = typeof (payment as any)?.last4 === "string" ? (payment as any).last4.trim() : "";

  // Legacy: cashapp is represented as `brand: "cashapp"` and `last4: "XXXX"`.
  if (brand === "cashapp") return "Cash App";
  if (last4.toUpperCase() === "XXXX") return "Cash App";
  if (last4) return `•••• ${last4}`;
  return undefined;
};

const hasSelectedCreditOption = (letter: RawMessage | null | undefined): boolean => {
  if (!letter) return false;
  const v =
    (letter as any)?.creditOption ??
    (letter as any)?.credit_option ??
    (letter as any)?.data?.creditOption ??
    (letter as any)?.data?.credit_option;
  if (typeof v === "number" && Number.isFinite(v) && v > 0) return true;
  if (typeof v === "string" && v.trim().length > 0) return true;
  return false;
};

const getSelectedCreditOptionInt = (letter: RawMessage | null | undefined): number => {
  if (!letter) return 0;
  const v =
    (letter as any)?.creditOption ??
    (letter as any)?.credit_option ??
    (letter as any)?.data?.creditOption ??
    (letter as any)?.data?.credit_option;
  const n = typeof v === "number" ? v : typeof v === "string" ? Number.parseInt(v, 10) : NaN;
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
};

const getMeta = (letter: RawMessage | null | undefined): any => {
  return (letter as any)?.meta ?? (letter as any)?.data?.meta ?? {};
};

const getSelectedOfferRedemptionId = (letter: RawMessage | null | undefined): string | null => {
  if (!letter) return null;
  const v =
    (letter as any)?.offerRedemptionId ??
    (letter as any)?.offer_redemption_id ??
    (letter as any)?.data?.offerRedemptionId ??
    null;
  return typeof v === "string" && v ? v : null;
};

const getOfferBonusCredits = (offer: DynamicPricingOffer | null | undefined): number => {
  if (!offer) return 0;
  const v = (offer as any).bonus_credits;
  return Math.max(0, Math.round(toNumber(v)));
};

const resolveSelectedOffer = (args: {
  offersByBundle: Record<string, DynamicPricingOffer> | null | undefined;
  creditOption: number;
  redemptionId: string | null;
}): DynamicPricingOffer | null => {
  const { offersByBundle, creditOption, redemptionId } = args;
  if (!offersByBundle) return null;
  if (redemptionId) {
    for (const key of Object.keys(offersByBundle)) {
      const offer = offersByBundle[key];
      if (offer && (offer as any).redemption_id === redemptionId) return offer;
    }
  }
  if (creditOption <= 0) return null;
  return offersByBundle[String(creditOption)] ?? null;
};

const getCents = (letter: RawMessage | null | undefined, key: string): number => {
  if (!letter) return 0;
  const v = (letter as any)?.[key] ?? (letter as any)?.data?.[key];
  return Math.max(0, Math.round(toNumber(v)));
};

export const getCheckoutRequirements = (args: {
  letter: RawMessage | null;
  user: PmUser | null;
  offersByBundle?: Record<string, DynamicPricingOffer> | null;
  noPhotosSelected?: boolean;
}): CheckoutRequirements => {
  const { letter, user, offersByBundle, noPhotosSelected } = args;

  const messageType = getMessageType(letter);
  const shippingMethod = getShippingMethod(letter);
  const hasFacility = hasFacilitySelected(letter);
  const pricingShippingMethod = shippingMethod === "emessage" && hasFacility ? "emessage" : "letter";
  const selectedPhotosCount = getSelectedPhotosCount(letter);
  const photoType = getLetterPhotoType(letter);
  const isResend = getIsResendFlag(letter);

  const meta = getMeta(letter);
  const creditsEnabledRaw =
    meta?.creditsEnabled != null || meta?.credits_enabled != null
      ? toBool(meta?.creditsEnabled ?? meta?.credits_enabled, true)
      : true;
  const creditsEnabled = !isResend && creditsEnabledRaw;
  const firstMessage =
    meta?.firstMessage != null || meta?.first_message != null
      ? toBool(meta?.firstMessage ?? meta?.first_message, false)
      : false;
  const freeFirstLetter =
    meta?.freeFirstLetter != null || meta?.free_first_letter != null
      ? toBool(meta?.freeFirstLetter ?? meta?.free_first_letter, false)
      : false;
  const winBackLetter =
    meta?.winBackLetter != null || meta?.win_back_letter != null
      ? toBool(meta?.winBackLetter ?? meta?.win_back_letter, false)
      : false;
  // Server prices win-back letters to $0 at send, same as the free first letter.
  const freeLetter = freeFirstLetter || winBackLetter;

  const priceAfterCouponCents = getCents(letter, "priceAfterCoupon") || getCents(letter, "price_after_coupon");
  const cashPaymentRequired = !freeLetter && priceAfterCouponCents > 0;

  const hasLetterCredit =
    meta?.hasLetterCredit != null || meta?.has_letter_credit != null
      ? toBool(meta?.hasLetterCredit ?? meta?.has_letter_credit, false)
      : false;

  const letterCreditsDue = Math.max(0, Math.round(toNumber(meta?.letterCreditsDue)));
  const letterCreditsAvailable = Math.max(0, Math.round(toNumber(meta?.letterCreditAmt)));
  const messageCreditsDue = Math.max(0, Math.round(toNumber(meta?.messageCreditCost)));
  const messageCreditsAvailable = user ? Math.max(0, Math.round(toNumber(user.messageCreditBalance))) : 0;

  // Legacy parity (confirm-order.ts + letters_payload_builder.rb):
  // - Never sum credit costs. When both fields exist, treat them as the same underlying cost.
  // - Prefer `letterCreditsDue` for gating; fallback to `messageCreditCost` when `letterCreditsDue` is missing/zero.
  // - Treat `meta.letterCreditAmt` as the authoritative "active pool" balance. On the backend, when the user does
  //   not have letter credits available, `letterCreditAmt` is populated with the message credit balance.
  // - Some client-side mocks omit `letterCreditAmt`; fall back to `user.messageCreditBalance` in that case.
  const activeCreditsDue = letterCreditsDue > 0 ? letterCreditsDue : messageCreditsDue;
  const activeCreditsAvailable =
    letterCreditsAvailable > 0 ? letterCreditsAvailable : messageCreditsAvailable;

  const isNewUser = !!(user && user.hasSentLetter === false);
  const isPostcard = messageType === "postcard";
  const isPrintAndMailLetter = messageType === "letter" && pricingShippingMethod !== "emessage";
  const isGlossy = photoType === "glossy";
  const isFirstMessagePrintAndMailCashPricing =
    isNewUser && firstMessage && isPrintAndMailLetter && !isResend && !isGlossy;
  const isFreeFirstMessagePrintAndMail = isFirstMessagePrintAndMailCashPricing && priceAfterCouponCents === 0;
  const isFirstMessagePostcardCashPricing = isNewUser && firstMessage && isPostcard && !isResend;

  // Legacy parity (confirm-order.ts):
  // - For new users sending their first print-and-mail letter, credits are only required when the photo type is glossy.
  // - For new users sending E-messages, credits purchase UI is always shown (even if balances look sufficient).
  // - Resends never require credit top-up.
  const creditsPurchaseRequiredByLegacy =
    !isResend &&
    ((isNewUser && pricingShippingMethod === "emessage") ||
      (isNewUser && isPrintAndMailLetter && firstMessage && isGlossy));

  // Standard behavior: treat letter credits and message credits as separate "balances" and require
  // a purchase if either pool is short. (confirm-order.ts uses this same predicate for offer gating.)
  const creditsPurchaseRequiredBase = !isResend && activeCreditsDue > activeCreditsAvailable;

  // `meta.creditsEnabled=false` is used for new users on a cash-pricing path (including first-message promos).
  // Even if meta contains non-zero credit counts, legacy checkout suppresses credits unless the glossy exception applies.
  const creditsPurchaseRequired =
    !freeLetter &&
    !isResend &&
    (creditsPurchaseRequiredByLegacy ||
      (!isFirstMessagePrintAndMailCashPricing && !isFirstMessagePostcardCashPricing && creditsPurchaseRequiredBase));

  // For checklist display, show the active pool (never sum both).
  const computedCreditsDue = activeCreditsDue;
  const computedCreditsAvailable = activeCreditsAvailable;
  const creditsCoveredByBalance = !isResend && activeCreditsDue > 0 && activeCreditsAvailable >= activeCreditsDue;
  const shouldShowCreditsCounts = !freeLetter && (creditsEnabled || creditsPurchaseRequired || creditsCoveredByBalance);
  const creditsDue = shouldShowCreditsCounts ? computedCreditsDue : 0;
  const creditsAvailable = shouldShowCreditsCounts ? computedCreditsAvailable : 0;

  const paymentRequired = creditsPurchaseRequired || cashPaymentRequired;

  const returnAddressComplete = hasPhysicalReturnAddress(letter);
  const postcardFrontRequired = messageType === "postcard";
  const postcardFrontComplete = !postcardFrontRequired || !!getPostcardFrontUrl(letter);
  const hasNoPhotosSelection = !!noPhotosSelected;
  const photoTypeRequired =
    messageType === "letter" && shippingMethod !== "emessage" && (selectedPhotosCount > 0 || hasNoPhotosSelection);
  const photoTypeComplete = !photoTypeRequired || hasNoPhotosSelection || !!photoType;
  const deliveryComplete = shippingMethod === "letter" || shippingMethod === "emessage";
  const facilityRequired = shippingMethod === "emessage" && !hasFacilitySelected(letter);
  const facilityComplete = !facilityRequired;
  const creditsRequired = paymentRequired && creditsPurchaseRequired;
  const selectedCreditOptionInt = getSelectedCreditOptionInt(letter);
  const singleOptionDisabled = messageType !== "postcard" && selectedPhotosCount > 1;
  const selectedOffer = resolveSelectedOffer({
    offersByBundle,
    creditOption: selectedCreditOptionInt,
    redemptionId: getSelectedOfferRedemptionId(letter),
  });
  const selectedOfferBonusCredits = getOfferBonusCredits(selectedOffer);
  const selectedEffectiveCredits =
    selectedCreditOptionInt > 1 ? selectedCreditOptionInt + selectedOfferBonusCredits : selectedCreditOptionInt;
  const creditsComplete =
    !creditsRequired ||
    (hasSelectedCreditOption(letter) &&
      selectedCreditOptionInt > 0 &&
      // Legacy semantics: creditOption "1" is a special "single message/postcard" purchase option
      // (not a literal 1-credit bundle). Treat it as complete if it isn't disabled.
      (selectedCreditOptionInt === 1
        ? !singleOptionDisabled
        : creditsAvailable + selectedEffectiveCredits >= creditsDue));
  // Slice 2.2 (D-013): when the server supplies the typed attached-state,
  // completion renders from it — an attached-but-declined method is no
  // longer "complete". Field absent (older payloads) or lever off falls
  // back to the historical object-presence inference.
  const hasPaymentObject = !!((letter as any)?.payment ?? (letter as any)?.data?.payment);
  const paymentState =
    typeof (letter as any)?.payment_state === "string" ? ((letter as any).payment_state as string) : null;
  const useTypedPaymentState = paymentState != null && paymentStateUiEnabled();
  const paymentFailureState =
    useTypedPaymentState &&
    (paymentState === "attached_retry_eligible" || paymentState === "attached_replacement_required")
      ? paymentState
      : null;
  const paymentComplete =
    !cashPaymentRequired ||
    (useTypedPaymentState
      ? paymentState === "attached_unverified" ||
        paymentState === "authorization_pending" ||
        paymentState === "authorized" ||
        paymentState === "settled" ||
        // HLD §5 / D-011: a soft decline keeps manual retry available — the
        // row stays complete (so Send now remains reachable) while the
        // detail carries the declined guidance. Only replacement-required
        // blocks submission, and the server heals that state the moment
        // the draft's method is replaced.
        paymentState === "attached_retry_eligible"
      : hasPaymentObject);
  const paymentSectionRequired = paymentRequired && cashPaymentRequired;
  const paymentDetail = getPaymentDetail(letter);
  const creditsDetail =
    creditsRequired
      ? selectedCreditOptionInt === 1
        ? singleOptionDisabled
          ? `${creditsDue} due / ${creditsAvailable} available`
          : messageType === "postcard"
            ? "1 postcard"
            : selectedPhotosCount > 0
              ? "1 message + photo"
              : "1 message"
        : selectedCreditOptionInt > 1
          ? selectedOfferBonusCredits > 0
            ? `${selectedCreditOptionInt} credits (+${selectedOfferBonusCredits} bonus)`
            : `${selectedCreditOptionInt} credits`
          : `${creditsDue} due / ${creditsAvailable} available`
      : undefined;

  const sections: CheckoutSection[] = [
    {
      key: "return_address",
      title: "Return address",
      required: true,
      complete: returnAddressComplete,
    },
    {
      key: "postcard_front",
      title: "Postcard front",
      required: postcardFrontRequired,
      complete: postcardFrontComplete,
      detail: postcardFrontRequired ? (postcardFrontComplete ? "Selected" : "Choose one") : undefined,
    },
    {
      key: "photo_type",
      title: "Photo type",
      required: photoTypeRequired,
      complete: photoTypeComplete,
      detail: hasNoPhotosSelection
        ? "No photos"
        : photoTypeRequired && photoType
          ? photoType === "glossy"
            ? "Glossy"
            : "Regular"
          : undefined,
    },
    {
      key: "delivery",
      title: "Delivery",
      required: true,
      complete: deliveryComplete,
      detail: shippingMethod === "emessage" ? "E-message" : "Print & Mail",
    },
    {
      key: "facility",
      title: "Confirm Facility",
      required: facilityRequired,
      complete: facilityComplete,
    },
    {
      key: "credits",
      title: "Credits",
      required: creditsRequired,
      complete: creditsComplete,
      detail: creditsDetail,
    },
    {
      key: "payment",
      title: "Payment",
      required: paymentSectionRequired,
      complete: paymentComplete,
      detail:
        paymentFailureState != null
          ? [
              paymentDetail,
              paymentFailureState === "attached_replacement_required"
                ? "Declined — use a different card"
                : "Declined — try again",
            ]
              .filter(Boolean)
              .join(" · ")
          : paymentDetail,
    },
  ].filter((s) => s.required);

  const nextRequiredKey =
    sections.find((s) => s.required && !s.complete)?.key ?? null;

  return {
    sections,
    nextRequiredKey,
    paymentRequired,
    paymentFailureState,
    creditsPurchaseRequired,
    creditsDue,
    creditsAvailable,
  };
};

export const formatUsd = (cents: number): string => {
  const dollars = Math.max(0, cents) / 100;
  return dollars.toLocaleString(undefined, { style: "currency", currency: "USD" });
};
